// src/components/common/ConfirmModal.js

import React, { useState, useEffect, useRef } from 'react';

// ---------------------------------------------------------------------------
// Generic confirm/prompt modal — replaces window.confirm() / window.prompt().
// Supports a plain confirm dialog, or one with a single input field
// (used for the "reactivate — new PIN" flow).
// ---------------------------------------------------------------------------
const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  variant = 'default', // 'default' | 'danger'
  onConfirm,
  onCancel,
  input // optional: { label, placeholder, type, maxLength, pattern, required, requiredError, patternError }
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setInputError('');
      // focus the input (or nothing) once the modal mounts
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (input) {
      if (input.required && !inputValue.trim()) {
        setInputError(input.requiredError || 'This field is required.');
        return;
      }
      if (input.pattern && !new RegExp(input.pattern).test(inputValue)) {
        setInputError(input.patternError || 'Invalid value.');
        return;
      }
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm();
  };

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {title && <h3 style={styles.title}>{title}</h3>}
        <p style={styles.message}>{message}</p>

        {input && (
          <div style={styles.inputWrapper}>
            {input.label && <label style={styles.inputLabel}>{input.label}</label>}
            <input
              ref={inputRef}
              type={input.type || 'text'}
              maxLength={input.maxLength}
              value={inputValue}
              placeholder={input.placeholder}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (inputError) setInputError('');
              }}
              onKeyDown={handleInputKeyDown}
              style={{
                ...styles.input,
                borderColor: inputError ? '#FF4747' : '#EDEDED'
              }}
            />
            {inputError && <div style={styles.inputError}>{inputError}</div>}
          </div>
        )}

        <div style={styles.actions}>
          <button style={styles.cancelButton} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            style={variant === 'danger' ? styles.confirmDanger : styles.confirmDefault}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position:       'fixed',
    inset:          0,
    backgroundColor:'rgba(0, 0, 0, 0.45)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius:    '10px',
    padding:         '24px',
    width:           '420px',
    maxWidth:        '90vw',
    boxShadow:       '0 12px 32px rgba(0,0,0,0.2)'
  },
  title: {
    fontSize:     '18px',
    fontWeight:   '600',
    color:        '#1C1C1E',
    margin:       '0 0 12px 0'
  },
  message: {
    fontSize:   '14px',
    lineHeight: '1.5',
    color:      '#3A3A3C',
    margin:     0
  },
  inputWrapper: {
    marginTop: '16px'
  },
  inputLabel: {
    display:      'block',
    fontSize:     '13px',
    fontWeight:   '500',
    color:        '#1C1C1E',
    marginBottom: '6px'
  },
  input: {
    width:           '100%',
    boxSizing:       'border-box',
    padding:         '10px 12px',
    borderRadius:    '6px',
    border:          '1px solid #EDEDED',
    fontSize:        '16px',
    letterSpacing:   '2px'
  },
  inputError: {
    color:     '#FF4747',
    fontSize:  '12px',
    marginTop: '6px'
  },
  actions: {
    display:        'flex',
    justifyContent: 'flex-end',
    gap:            '10px',
    marginTop:      '24px'
  },
  cancelButton: {
    backgroundColor: '#EDEDED',
    color:           '#1C1C1E',
    border:          'none',
    padding:         '9px 18px',
    borderRadius:    '6px',
    cursor:          'pointer',
    fontWeight:      '500',
    fontSize:        '14px'
  },
  confirmDefault: {
    backgroundColor: '#529EDF',
    color:           'white',
    border:          'none',
    padding:         '9px 18px',
    borderRadius:    '6px',
    cursor:          'pointer',
    fontWeight:      '500',
    fontSize:        '14px'
  },
  confirmDanger: {
    backgroundColor: '#FF4747',
    color:           'white',
    border:          'none',
    padding:         '9px 18px',
    borderRadius:    '6px',
    cursor:          'pointer',
    fontWeight:      '500',
    fontSize:        '14px'
  }
};

export default ConfirmModal;